import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import coursePlaceHolderImage from 'assets/images/courses-placeholder-small.png';
import { DotsThreeVertical, Eye, SignOut } from 'phosphor-react';
import React, { useState } from 'react';
import { TCourse } from 'types';

interface CourseCardProps {
  course: TCourse;
  onEnroll: (courseId: string) => Promise<void>;
  onUnenroll: (courseId: string) => void; // Changed return type since it now opens a dialog instead of performing API call
  onViewCourse: (courseId: string) => void;
  isProcessing: boolean;
  processingCourseId: string | null;
}

export const CourseCard: React.FC<CourseCardProps> = ({
  course,
  onEnroll,
  onUnenroll,
  onViewCourse,
  isProcessing,
  processingCourseId,
}) => {
  const isEnrolled = course.meta?.isEnrolled;
  const isPaid = course.meta?.paymentRequired;
  const canUnenroll = course.meta?.canUnenroll ?? false;
  const isCurrentlyProcessing = isProcessing && processingCourseId === course.id;

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleViewCourse = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    onViewCourse(course.id);
  };

  const handleUnenroll = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    onUnenroll(course.id);
  };

  const handleEnroll = () => {
    onEnroll(course.id);
  };

  const handleCardClick = () => {
    onViewCourse(course.id);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        },
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
      onClick={handleCardClick}
    >
      <CardMedia
        component="img"
        height="160"
        image={course.image?.mediumUrl || coursePlaceHolderImage}
        alt={course.name}
        sx={{ objectFit: 'cover' }}
      />
      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          position: 'relative',
        }}
      >
        {/* Menu button */}
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: 'rgba(0,0,0,0.04)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
          }}
          onClick={handleMenuClick}
        >
          <DotsThreeVertical size={20} />
        </IconButton>

        {/* Action menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={open}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'course-actions-button',
            dense: true,
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleViewCourse} sx={{ py: 1.5 }}>
            <Eye size={18} style={{ marginRight: 8 }} weight="regular" />
            View Course
          </MenuItem>

          {isEnrolled && canUnenroll && (
            <MenuItem onClick={handleUnenroll} sx={{ py: 1.5, color: 'error.main' }}>
              <SignOut size={18} style={{ marginRight: 8 }} weight="regular" />
              Unenroll
            </MenuItem>
          )}
        </Menu>

        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: 1.5,
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            lineHeight: 1.3,
            minHeight: '2.6em',
            pr: 4, // Space for the menu button
          }}
        >
          {course.name}
        </Typography>

        <Box sx={{ display: 'flex', mb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${course.assignmentsCount} Assignment${course.assignmentsCount !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          />
          {isPaid && (
            <Chip label="Paid" size="small" color="secondary" variant="outlined" sx={{ borderRadius: '8px' }} />
          )}
          {isEnrolled && (
            <Chip label="Enrolled" size="small" color="success" variant="outlined" sx={{ borderRadius: '8px' }} />
          )}
        </Box>

        {course.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              lineHeight: 1.5,
              height: '4.5em',
            }}
          >
            {course.description}
          </Typography>
        )}

        {!isEnrolled && (
          <Button
            variant="contained"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              handleEnroll();
            }}
            disabled={isCurrentlyProcessing}
            sx={{
              mt: 'auto',
              py: 1.2,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              boxShadow: '0 3px 8px rgba(0,0,0,0.08)',
            }}
          >
            {isCurrentlyProcessing ? <CircularProgress size={24} color="inherit" /> : 'Enroll'}
          </Button>
        )}

        {isEnrolled && (
          <Button
            variant="outlined"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onViewCourse(course.id);
            }}
            sx={{
              mt: 'auto',
              py: 1.2,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            Continue Learning
          </Button>
        )}
      </CardContent>
    </Card>
  );
};